import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import moment from 'moment';

import {
    dataDetailsOpenModal,
    transactionsResultRequest,
    searchEntityRequest,
    clearRedux,
} from "Redux/actions";

//Components
import { Grid, Typography, Container, Paper, Divider, Chip } from "@mui/material";
import { getDateFormatted, getPMUser } from 'Core/data/Helpers';
import { optionsTimeFrame } from 'Core/defaultValues';
import { getTimeFrame } from 'Utils/helpers';
import DataView from 'Components/DataView';
import { Select, Tooltip } from '@gpstrackit/react-controls';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton } from "@mui/material";

//Views
import DataDetails from '../../.components/dataDetails'

//Styles
import './results.scss';

const transactionsResults = () => {
    const { messages } = useIntl();
    let optionsTime = optionsTimeFrame();
    const tableRef = useRef();
    const dispatch = useDispatch();
    const { transactionid } = useParams();

    const { loading, nextToken } = useSelector(state => state.resultsRedux);
    const transactions = useSelector(state => state.resultsRedux.transactions);
    const openModalDataDetails = useSelector(state => state.dataDetailsRedux.openModal);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);

    const [timeFrame, setTimeFrame] = useState(optionsTime[2]);
    const [search, setSearch] = useState(null);
    const [tags, setTags] = useState([]);
    const [startDate, setStartDate] = useState(moment().day(0).startOf('day').toDate());
    const [endDate, setEndDate] = useState(moment.max(moment()).toDate());

    const [columns, setColumns] = useState([
        {
            key: "CreatedOn",
            title: messages['transactions_result_created'],
            sort: false,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "transactions_result_created",
            render: (item) => {
                return <span>{item?.CreatedOn && getDateFormatted(item?.CreatedOn, "long", "datetime")}</span>
            }
        },
        {
            key: "TransactionId",
            title: messages['transactions_result_transaction'],
            sort: false,
            width: 300,
            show: true,
            locked: false,
            resize: true,
            code: "transactions_result_transaction",
        },
        {
            key: "Status",
            title: messages['transactions_result_status'],
            sort: false,
            width: 100,
            show: true,
            locked: false,
            resize: true,
            code: "transactions_result_status",
            render: (item) => {
                const currentState = getStatus(item?.Status);
                return (
                    <span>
                        <Chip
                            label={currentState.label}
                            size="small"
                            style={{
                                backgroundColor: currentState.color,
                                color: "#ffffff"
                            }}
                        />
                    </span>
                )
            }
        },
        {
            key: "Type",
            title: messages['transactions_result_type'],
            sort: false,
            width: 100,
            show: true,
            locked: false,
            resize: true,
            code: "transactions_result_type",
        },
        {
            key: "Data",
            title: messages['transactions_result_data'],
            sort: false,
            width: 100,
            show: true,
            locked: false,
            resize: true,
            code: "transactions_result_data",
            render: (item) => {
                return (
                    <div>
                        <Tooltip
                            title={messages['transactions_result_show_data']}
                        >
                            <IconButton onClick={() => dispatch(dataDetailsOpenModal(item?.Data))} size="large">
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            key: "Message",
            title: messages['transactions_result_message'],
            sort: false,
            width: 350,
            show: true,
            locked: false,
            resize: true,
            code: "transactions_result_message",
        },
        {
            key: "CreatedBy",
            title: messages['transactions_result_created_by'],
            sort: false,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "transactions_result_created_by",
            render: (item) => {
                let label = item?.CreatedBy ? getPMUser(item?.CreatedBy, pmUsers) : ""
                return <span>{label}</span>
            }
        }
    ])

    // functions
    const getStatus = (current) => {
        let status = {
            SUCCESS: {
                label: messages['transactions_result_success'],
                color: '#00a674'
            },
            FAILURE: {
                label: messages['transactions_result_failure'],
                color: 'red'
            },
            PENDING: {
                label: messages['transactions_result_pending'],
                color: '#00AAFB'
            }
        }
        return status[current] || status['PENDING']
    }

    const transactionsHandler = (newTime, newTags, refresh) => {
        let currentConditions = tableRef?.current?.conditions;
        if (newTime || newTags || refresh) {
            dispatch(clearRedux("TRANSACTIONS_RESULT", { nextToken: null, transactions: [] }));
        }
        let currentTags = tags
        if (newTags) {
            //Delete Tags
            let deleteTags = currentConditions.findIndex((item) => item.fields[0] === "openSearch");
            deleteTags >= 0 && currentConditions.splice(deleteTags, 1);
            currentConditions.push({ fields: ["openSearch"], terms: newTags });
            tableRef?.current?.conditionsHandler(currentConditions);
            setTags(newTags);
            currentTags = newTags
        }
        let currentOpenSearch = currentTags;

        let payload = {
            dateRange: {
                start: newTime?.start || startDate,
                end: newTime?.end || endDate,
            },
            opensearch: currentOpenSearch
        }
        if (nextToken && !newTime && !newTags && !refresh) {
            payload.nextToken = nextToken
        }

        !nextToken && !refresh && dispatch(clearRedux("TRANSACTIONS_RESULT", { transactions: [] }))
        dispatch(transactionsResultRequest(payload))
    }

    const handleTimeFrame = (type) => {
        setTimeFrame(type);
        const { start, end } = getTimeFrame(type.Id);
        setStartDate(start);
        setEndDate(end);
        transactionsHandler({ start, end }, [])

        //save preferences
        let currentConditions = tableRef?.current?.conditions;

        //Delete
        let deleteTimeFrame = currentConditions.findIndex((item) => item.fields[0] === "timeframe");
        deleteTimeFrame >= 0 && currentConditions.splice(deleteTimeFrame, 1);

        let deleteTags = currentConditions.findIndex((item) => item.fields[0] === "openSearch");
        deleteTags >= 0 && currentConditions.splice(deleteTags, 1);

        //Add
        currentConditions.push({ fields: ["timeframe"], terms: { start, end, type } })
        tableRef?.current?.conditionsHandler(currentConditions);
        tableRef?.current?.setTags([]);
    }

    useEffect(() => {

        if (transactionid !== "-1") {
            setSearch(transactionid);
        }

        return () => {
            dispatch(clearRedux("TRANSACTIONS_RESULT"))
        }
    }, [transactionid])

    useEffect(() => {
        if (search || search === "") {
            const timer = setTimeout(() => {
                transactionsHandler(null, [search]);
                //Save preference
                let currentConditions = tableRef?.current?.conditions;

                //Delete
                let deleteOpenSearch = currentConditions.findIndex((item) => item.fields[0] === "opensearch");
                deleteOpenSearch >= 0 && currentConditions.splice(deleteOpenSearch, 1);

                //Add
                currentConditions.push({ fields: ["opensearch"], terms: [search] });
                tableRef?.current?.conditionsHandler(currentConditions);
                tableRef?.current?.setTags([{ transactionId: search }]);
            }, 500)
            return () => clearTimeout(timer);
        }
        return () => {
            dispatch(clearRedux("TRANSACTIONS_RESULT", { transactions: [] }))
        }
    }, [search])

    useEffect(() => {
        (pmUsers?.length === 0) && dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, [pmUsers]);

    useEffect(() => {
        if (pmUsers.length > 0) {
            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                render: item.key === "CreatedBy" ? (item) => {
                    let label = item?.CreatedBy ? getPMUser(item?.CreatedBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.render,
            }))
            setColumns([...currentColumns])
        }
    }, [pmUsers])

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={12}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['transactions_result_title']}
                        </Typography>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataView
                        ref={tableRef}
                        name="pm-transactions-results"
                        exportName="transactionsresults"
                        noPreferences
                        disableItemSelection
                        topheight={200}
                        noSplit
                        noFilters
                        externalLoading={loading}
                        externalData={transactions}
                        externalRefresh={() => transactionsHandler(null, null, true)}
                        columns={columns}
                        onChangeColumns={(items) => setColumns(items)}
                        infiniteScroll
                        nextToken={nextToken}
                        extraFilters={() => {
                            return (
                                <div className="extra-filters-tab">
                                    <Select
                                        id="timeframe"
                                        placeholder={messages['activities_main_time_frame']}
                                        options={optionsTime}
                                        value={timeFrame}
                                        textField="Nametag"
                                        valueField="Id"
                                        onChange={(items = []) => handleTimeFrame(items)}
                                    />
                                </div>
                            )
                        }}
                        searchOptionValue="transactionId"
                        searchOptionLabel="transactionId"
                        searchConditions={["transactionId"]}
                        onChangeConditions={(newTags, newConditions) => {

                            if (newTags) {
                                let tags = [];
                                if (newTags.length > 0)
                                    tags.push(newTags[0].transactionId)

                                transactionsHandler(null, tags)
                            } else {
                                let payload = {
                                    dateRange: {
                                        start: startDate,
                                        end: endDate,
                                    },
                                    opensearch: [],
                                    nextToken: nextToken,
                                    conditionOperator: "AND"
                                }

                                //Tags
                                let validateSearch = newConditions && newConditions?.find((tag) => tag?.fields && tag?.fields[0] === "openSearch");
                                if (validateSearch) {
                                    setTags(validateSearch?.terms || [])
                                    tableRef?.current?.setTags(validateSearch?.terms);
                                    payload.opensearch = validateSearch?.terms
                                }
                                //Time Frame
                                let validateTimeFrame = newConditions && newConditions?.find((tag) => tag?.fields && tag?.fields[0] === "timeframe");
                                if (validateTimeFrame) {
                                    setTimeFrame(validateTimeFrame?.terms?.type);
                                    setStartDate(validateTimeFrame?.terms?.start);
                                    setEndDate(validateTimeFrame?.terms?.end);
                                    payload.dateRange.start = validateTimeFrame?.terms?.start || startDate;
                                    payload.dateRange.end = validateTimeFrame?.terms?.end || endDate;

                                    dispatch(transactionsResultRequest(payload))
                                }

                            }
                        }}
                        onChangePage={() => transactionsHandler()}

                    />
                </Grid>

                {openModalDataDetails && <DataDetails />}
            </Paper>
        </Container>
    )
}

export default transactionsResults;